<template>
  <div>
    <v-container fluid>
      <StandardCard
        :title="store.state.pages.Requests.title || 'Requests'"
        main-page-title
      >
        <StandardPageTemplate
          @create="$refs.initiateRequestDialog.open()"
          :margin="0"
          container-class="px-0 py-0 bg-white"
          resource-name="request"
          createable
          no-title
        >
          <DataTableServer
            v-if="forms"
            @click:row="handleRowClick"
            @update:page="handlePageChange"
            :headers="tableHeaders"
            :hide-default-footer="!totalItemCount"
            :items="forms"
            :items-length="totalItemCount"
            :items-per-page="pageSize"
            :loading="processing"
            class="b-radius-0 b-1 bc-outlined-gray"
            item-key="id"
            no-data-text="No requests found"
            fixed-header
            hover
          >
            <template #filters>
              <FilterMenu
                v-if="requestableSchemas.length > 1"
                ref="typeFilter"
                :actions="false"
                :active="!!filters.schema_id"
                :title="filteredSchemaType || 'Type'"
                default-button-class=""
              >
                <template #card>
                  <v-autocomplete
                    v-model="filters.schema_id"
                    @update:model-value="$refs.typeFilter.close()"
                    :item-title="(schema) => $bct(schema.name)"
                    :items="requestableSchemas"
                    :placeholder="$bct('Search by type')"
                    item-value="id"
                    variant="filled"
                    clearable
                    left
                  />
                </template>
              </FilterMenu>

              <FilterMenu
                v-if="providers.length > 0"
                ref="providerFilter"
                :actions="false"
                :active="!!filters.provider_id"
                :title="filteredProviderName || 'Location'"
                default-button-class=""
              >
                <template #card>
                  <v-autocomplete
                    v-model="filters.provider_id"
                    @update:model-value="$refs.providerFilter.close()"
                    :item-title="
                      (provider) =>
                        (provider.name + ' - ' + provider.address1 + ')').substring(0, 45)
                    "
                    :items="providers"
                    :placeholder="$bct('Search by name or address')"
                    item-value="id"
                    variant="filled"
                    clearable
                    left
                  />
                </template>
              </FilterMenu>

              <!-- TODO: Add form status filtering -->
              <!-- TODO: Add created by filtering -->
            </template>

            <template #[`item.id`]="{ item }">
              {{ shortId(item.id) }}
            </template>

            <template #[`item.type`]="{ item }">
              {{ $bct(getSchemaName(item.relationships.schema.data.id)) }}
            </template>

            <template #[`item.location`]="{ item }">
              {{ getProviderName(item.relationships.provider.data.id) }}
            </template>

            <template #[`item.attributes.status`]="{ item }">
              {{ $bct(item.attributes.status) }}
            </template>

            <template #[`item.created_by`]="{ item }">
              {{ getMemberName(item.relationships.member.data?.id) }}
            </template>

            <template #[`item.submitted_at`]="{ item }">
              <LongDateTime :date="item.attributes.submitted_at" />
            </template>
          </DataTableServer>
        </StandardPageTemplate>
      </StandardCard>
    </v-container>

    <FormReviewDialog ref="reviewDialog" />

    <FormDialog
      @close="loadForms()"
      @save="loadForms()"
      ref="updateRequestDialog"
      :provider-id="updateRequestDialogProviderId"
      :schema-id="updateRequestDialogSchemaId"
      draft
    />

    <ResourceDialog
      @save="continueToForm"
      ref="initiateRequestDialog"
      :fields="initialRequestFields"
      save-button-text="Continue"
      title="Submit a request"
      close-on-save
    />
  </div>
</template>

<script setup>
import _ from 'lodash'; // eslint-disable-line id-length
import { useStore } from 'vuex';

import DataTableServer from '@/shared/components/table/DataTableServer.vue';
import FilterMenu from '@/shared/components/form/FilterMenu.vue';
import FormDialog from '@/shared/components/form/FormDialog.vue';
import FormReviewDialog from '@/shared/components/form/FormReviewDialog.vue';
import LongDateTime from '@/shared/components/LongDateTime.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import { shortId } from '@/plugins/filters';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const $bct = inject('translator');

const { api: Api } = useApi();
const eventBus = useEventBus();
const store = useStore();

const filters = reactive({
  provider_id: null,
  schema_id: null,
});
const forms = ref(null);
const includedMembers = ref([]);
const includedProviders = ref([]);
const includedSchemas = ref([]);
const updateRequestDialogProviderId = ref(null);
const updateRequestDialogSchemaId = ref(null);
const members = ref([]);
const page = ref(1);
const processing = ref(true);
const providers = ref([]);
const reviewDialog = ref(null);
const updateRequestDialog = ref(null);

const pageSize = ref(100);
const tableHeaders = ref([
  {
    title: 'ID',
    value: 'id',
  },
  {
    title: 'Type',
    value: 'type',
  },
  {
    title: 'Location',
    value: 'location',
  },
  {
    title: 'Status',
    value: 'attributes.status',
    align: 'center',
  },
  {
    title: 'Created by',
    value: 'created_by',
  },
  {
    title: 'Submitted at',
    value: 'submitted_at',
    align: 'end',
  },
]);

const totalItemCount = ref(0);
const requestableSchemas = computed(() => {
  return _.orderBy(
    store.state.providerRequestableFormSchemas,
    [(schema) => schema.name.toLowerCase()],
    ['asc'],
  );
});

const filteredProviderName = computed(() => {
  if (!filters.provider_id) return null;

  return providers.value.find((provider) => provider.id === filters.provider_id)?.name;
});

const filteredSchemaType = computed(() => {
  if (!filters.schema_id) return null;

  return requestableSchemas.value.find((schema) => schema.id === filters.schema_id)?.name;
});

const initialRequestFields = computed(() => {
  const fields = [
    {
      text: 'Select request type',
      value: 'schema_id',
      items: requestableSchemas.value,
      itemText: (item) => $bct(item.name),
      itemValue: 'id',
      required: true,
    },
  ];

  if (providers.value.length > 1) {
    fields.push({
      text: 'Select a site',
      value: 'provider_id',
      items: providers.value,
      itemText: 'name',
      itemValue: 'id',
      required: true,
    });
  }

  return fields;
});

watch(
  filters,
  (newValue) => {
    if (newValue && page.value !== 1) page.value = 1;
    void loadForms();
  },
  { immediate: true, deep: true },
);

function continueToForm(formData) {
  updateRequestDialogSchemaId.value = formData.schema_id;
  updateRequestDialogProviderId.value = formData.provider_id || providers.value[0].id;

  updateRequestDialog.value.open();
}

function getMemberName(id) {
  if (!id) return;

  return includedMembers.value.find((member) => member.id === id)?.attributes?.name || 'Unknown';
}

function getProviderName(id) {
  if (!id) return;

  return (
    includedProviders.value.find((provider) => provider.id === id)?.attributes?.name || 'Unknown'
  );
}

async function handlePageChange(newPage) {
  await loadForms(newPage);
}

function handleRowClick(event, { item }) {
  reviewRequest(item);
}

function getSchemaName(id) {
  return includedSchemas.value.find((schema) => schema.id === id)?.attributes?.name || 'Unknown';
}

function load() {
  void loadProviders();
  void loadMembers();
  void loadForms();
}

async function loadMembers() {
  const response = await Api.manager.member.index({});
  members.value = response.data;
}

async function loadProviders() {
  const response = await Api.manager.provider.index();
  providers.value = response.data;
}

async function loadForms(pageValue = page.value) {
  processing.value = true;

  const params = {
    'filter[schema]':
      filters.schema_id || requestableSchemas.value.map((schema) => schema.id).join(','),
    // Note: Currently workable, but not leveraging json api
    'page[offset]': (pageValue - 1) * pageSize.value,
    include: 'member,provider,schema',
  };
  if (filters.provider_id) params['filter[provider]'] = filters.provider_id;

  const { data } = await Api.manager.form.index(params);

  includedMembers.value = data.included?.filter((include) => include.type === 'member') || [];
  includedProviders.value = data.included?.filter((include) => include.type === 'provider') || [];
  includedSchemas.value = data.included?.filter((include) => include.type === 'schema') || [];

  forms.value = data.data || [];

  const parsedTotalCount = parseInt(data.meta?.total_count);
  const parsedPageLimit = parseInt(data.meta?.page_limit);

  if (!Number.isNaN(parsedTotalCount)) totalItemCount.value = parsedTotalCount;
  if (!Number.isNaN(parsedPageLimit)) pageSize.value = parsedPageLimit;

  processing.value = false;
}

function reviewRequest(request) {
  const form = {
    id: request.id,
    name: getSchemaName(request.relationships.schema.data.id),
    schema_id: request.relationships.schema.data.id,
    custom: request.attributes.custom,
    providerId: request.relationships.provider.data.id,
  };

  if (!request.attributes.submitted_at) {
    if (request.relationships.member.data?.id !== store.state.profile.id) {
      eventBus.chime('Cannot update a request in progress created by another user');
      return;
    }

    updateRequestDialogProviderId.value = form.providerId;
    updateRequestDialogSchemaId.value = form.schema_id;

    updateRequestDialog.value.open(form);
  } else {
    reviewDialog.value.open(form);
  }
}

onMounted(load);
</script>
